(() => {
  const root = () => window.Shopify?.routes?.root || '/';

  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  const getSectionMarkup = (state, sectionName) => state?.sections?.[sectionName] || null;

  const getParsedSection = (state, sectionName) => {
    const markup = getSectionMarkup(state, sectionName);
    return markup ? parseHTML(markup) : null;
  };

  const createFetchConfig = ({ method = 'GET', body, headers = {} } = {}) => ({
    method,
    headers: {
      Accept: 'application/json',
      ...headers,
    },
    ...(body ? { body } : {}),
  });

  const getErrorMessage = (error, fallback = '') => {
    if (typeof error === 'string' && error.trim()) return error.trim();

    if (Array.isArray(error)) {
      const firstMessage = error.map((entry) => getErrorMessage(entry, '')).find(Boolean);
      return firstMessage || fallback;
    }

    if (error && typeof error === 'object') {
      const firstMessage = Object.values(error)
        .map((entry) => getErrorMessage(entry, ''))
        .find(Boolean);

      return firstMessage || fallback;
    }

    return fallback;
  };

  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  const getFocusableElements = (container) =>
    [...container.querySelectorAll(focusableSelector)].filter(
      (element) => !element.hidden && element.getClientRects().length > 0
    );

  const trapFocus = (event, container) => {
    if (event.key !== 'Tab') return;

    const focusableElements = getFocusableElements(container);
    if (focusableElements.length === 0) {
      event.preventDefault();
      container.focus({ preventScroll: true });
      return;
    }

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    const focusIsOutsideContainer =
      document.activeElement === container || !container.contains(document.activeElement);

    if (event.shiftKey && (document.activeElement === firstElement || focusIsOutsideContainer)) {
      event.preventDefault();
      lastElement.focus();
    } else if (!event.shiftKey && (document.activeElement === lastElement || focusIsOutsideContainer)) {
      event.preventDefault();
      firstElement.focus();
    }
  };

  const setSiblingsInert = (element, inertState = []) => {
    let currentElement = element;

    while (currentElement?.parentElement && currentElement !== document.body) {
      [...currentElement.parentElement.children].forEach((sibling) => {
        if (sibling === currentElement) return;

        inertState.push({ element: sibling, wasInert: sibling.inert });
        sibling.inert = true;
      });

      currentElement = currentElement.parentElement;
    }

    return inertState;
  };

  const restoreSiblingsInert = (inertState = []) => {
    inertState.forEach(({ element, wasInert }) => {
      if (element.isConnected) element.inert = wasInert;
    });
  };

  if (!customElements.get('quantity-input')) {
    class QuantityInput extends HTMLElement {
      constructor() {
        super();

        this.input = this.querySelector('input');
        if (!this.input) return;

        this.changeEvent = new Event('change', { bubbles: true });
        this.input.addEventListener('change', this.onInputChange.bind(this));

        this.querySelectorAll('button').forEach((button) => {
          button.addEventListener('click', this.onButtonClick.bind(this));
        });
      }

      connectedCallback() {
        this.validateQtyRules();
      }

      onInputChange() {
        this.validateQtyRules();
      }

      onButtonClick(event) {
        if (!this.input) return;

        event.preventDefault();

        const previousValue = this.input.value;

        if (event.currentTarget.name === 'plus') {
          this.input.stepUp();
        } else {
          this.input.stepDown();
        }

        if (previousValue !== this.input.value) {
          this.input.dispatchEvent(this.changeEvent);
        }
      }

      validateQtyRules() {
        if (!this.input) return;

        const value = Number.parseInt(this.input.value, 10);
        const min = Number.parseInt(this.input.min, 10);
        const max = Number.parseInt(this.input.max, 10);
        const minusButton = this.querySelector('button[name="minus"]');
        const plusButton = this.querySelector('button[name="plus"]');

        if (minusButton && Number.isFinite(min)) {
          minusButton.toggleAttribute('disabled', value <= min);
          minusButton.classList.toggle('disabled', value <= min);
        }

        if (plusButton && Number.isFinite(max)) {
          plusButton.toggleAttribute('disabled', value >= max);
          plusButton.classList.toggle('disabled', value >= max);
        }
      }
    }

    customElements.define('quantity-input', QuantityInput);
  }

  window.PureTheme = {
    createFetchConfig,
    getFocusableElements,
    getErrorMessage,
    getParsedSection,
    getSectionMarkup,
    parseHTML,
    restoreSiblingsInert,
    root,
    setSiblingsInert,
    trapFocus,
  };
})();
